/** Roll historical page_keys into the live surface they belong to. */
const PAGE_KEY_ALIASES: Record<string, string> = {
  journalPost: "journal",
  /** Older footer nav tracked Blog as entity_id `blog`. */
  blog: "journal",
  platform: "content",
};

export function canonicalWebsiteAnalyticsPageKey(pageKey: string): string {
  const raw = String(pageKey || "").trim();
  if (!raw || raw === "unknown") return "other";
  return PAGE_KEY_ALIASES[raw] || raw;
}

export type WebsiteAnalyticsLabelFn = (pageKey: string) => string;

/**
 * Merge page breakdown rows: alias historical keys, sum counts.
 * `isKnownPage` / `isLiveCustomPage` decide when orphans become "other".
 */
export function mergeWebsiteAnalyticsPageBreakdown(
  rows: Array<{ page_key: string; page_views: number; event_count: number }>,
  labelFor: WebsiteAnalyticsLabelFn,
  opts?: {
    isOrphanPageKey?: (pageKey: string) => boolean;
  },
): Array<{ page: string; page_views: number; interactions: number }> {
  const totals = new Map<
    string,
    { page_views: number; interactions: number }
  >();

  for (const row of rows || []) {
    let key = canonicalWebsiteAnalyticsPageKey(row.page_key);
    if (opts?.isOrphanPageKey?.(key)) {
      key = "other";
    }
    const cur = totals.get(key) || { page_views: 0, interactions: 0 };
    cur.page_views += Number(row.page_views || 0);
    cur.interactions += Number(row.event_count || 0);
    totals.set(key, cur);
  }

  return [...totals.entries()]
    .map(([key, v]) => ({
      page: labelFor(key),
      page_views: v.page_views,
      interactions: v.interactions,
    }))
    .sort(
      (a, b) =>
        b.page_views - a.page_views || b.interactions - a.interactions,
    );
}

/**
 * Merge nav clicks by destination `entity_id` so renamed About / hero Shop CTAs
 * combine with header/footer clicks to the same page.
 */
export function mergeWebsiteAnalyticsNavClicks(
  rows: Array<{ entity_label: string; entity_id: string; count: number }>,
  labelFor: WebsiteAnalyticsLabelFn,
): Array<{ menu: string; clicks: number }> {
  const byId = new Map<string, { label: string; clicks: number }>();

  for (const row of rows || []) {
    const rawId = String(row.entity_id || "").trim() || "_";
    const id =
      rawId === "_"
        ? "_"
        : canonicalWebsiteAnalyticsPageKey(rawId);
    const storedLabel = String(row.entity_label || id).trim() || id;
    const live = id !== "_" ? labelFor(id) : storedLabel;
    const label =
      id !== "_" && live && live !== id ? live : storedLabel || live;
    const cur = byId.get(id) || { label, clicks: 0 };
    cur.clicks += Number(row.count || 0);
    if (id !== "_" && live && live !== id) cur.label = live;
    byId.set(id, cur);
  }

  return [...byId.values()]
    .map((v) => ({ menu: v.label, clicks: v.clicks }))
    .sort((a, b) => b.clicks - a.clicks);
}
